import React, {
  createContext, useState, useEffect, useContext
} from 'react';
import dynamicFederation from 'utils/dynamicFederation';
import { Spin } from 'antd';
import styled from 'styled-components';
import { LoadingOutlined } from "@ant-design/icons";

const ModulesContext = createContext([]);
const loadedScripts = new Set();

const loadRemoteScript = async (moduleUrl) => {
  if (loadedScripts.has(moduleUrl)) {
    return;
  }

  await new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = moduleUrl;

    script.onload = () => {
      loadedScripts.add(moduleUrl);
      resolve();
    };

    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const loadModuleRoutes = async (module) => {
  try {    
    await loadRemoteScript(module.moduleUrl);
    
    const routes = await dynamicFederation(module.containerName, './routes');

    return routes.default || routes;
  } catch (error) {
    console.error(`Error loading routes for module ${module.containerName}:`, error);

    return null;
  }
};

const generatePageModules = async (modules) => {
  const pageModules = [];
  const processedContainers = new Set();

  for (const module of modules) {
    try {
      if (!processedContainers.has(module.containerName)) {
        processedContainers.add(module.containerName);
        
        const routes = await loadModuleRoutes(module);
        
        if (routes?.pages) {
          Object.entries(routes.pages).forEach(([key, pageConfig]) => {
            const pageModule = {
              id: `${module.containerName}-${key}`,
              moduleUrl: module.moduleUrl,
              containerName: module.containerName,
              componentOrder: module.componentOrder || 1,
              componentPath: pageConfig.component,
              componentPosition: 'page',
              componentPage: pageConfig.path
            };
            pageModules.push(pageModule);
          });
        }
      }
    } catch (error) {
      console.error(`Error processing module ${module.id}:`, error);
    }
  }

  return [...modules, ...pageModules];
};

const LoadingContainer = styled.div`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #FFF;
`;

export const ModulesProvider = ({ children }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const antIcon = <LoadingOutlined style={{ fontSize: 24, color: "#333" }} spin />;

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${	process.env.REACT_APP_MODULES_ENDPOINT }/${window.location.hostname}/modules.json`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const baseModules = Array.isArray(data.modules) ? data.modules : [];

        const allModules = await generatePageModules(baseModules);

        setModules(allModules);
      } catch (error) {
        console.error('Error in ModulesProvider:', error);
        setError(error);
        setModules([]);
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, []);

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" indicator={antIcon} />
      </LoadingContainer>
    );
  }

  if (error) {
    return (
      <div>
        Erro ao carregar módulos:
        {error.message}
      </div>
    );
  }

  return (
    <ModulesContext.Provider value={modules}>
      {children}
    </ModulesContext.Provider>
  );
};

export const ensureScriptLoaded = loadRemoteScript;

export const useModulesContext = () => {
  const context = useContext(ModulesContext);

  if (!context) {
    console.warn('useModulesContext must be used within a ModulesProvider');
  }

  return Array.isArray(context) ? context : [];
};
